import { getLocaleString, translateBackend } from '@assets/i18n/config';
import type { colorMap } from '@ovComponents/2-component/badge/badge';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { TableCell } from '@ovComponents/2-component/table/table';
import { UserContext } from '@providers/userContextProvider';
import { Tooltip } from 'hephaestus/web';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { stateColorMap, TableHeaderKeys } from '../constants';
import type { AlertBannerRow } from '../types';
import AlertBannerActionCell from './alertBannerActionCell';

export const BannerCell = ({
  alertBanner,
  headerKey,
  isFirst,
  refetch,
}: {
  alertBanner: AlertBannerRow;
  headerKey: TableHeaderKeys;
  isFirst?: boolean;
  refetch: () => void;
}) => {
  const { t } = useTranslation(['alertBanners']);
  const { activeOrganization } = useContext(UserContext);

  switch (headerKey) {
    case TableHeaderKeys.Name:
      return <TableCell isFirst={isFirst}>{alertBanner.name}</TableCell>;

    case TableHeaderKeys.RoleProfiles:
      return (
        <TableCell isFirst={isFirst} center>
          {alertBanner.roleProfiles
            .map((roleProfile) => translateBackend(roleProfile.translatedName))
            .join(', ')}
        </TableCell>
      );
    case TableHeaderKeys.LastUpdated:
      return (
        <TableCell isFirst={isFirst} center>
          {new Date(alertBanner.updatedAt).toLocaleDateString(
            getLocaleString(),
          )}
        </TableCell>
      );
    case TableHeaderKeys.ActiveDateRange:
      return (
        <TableCell isFirst={isFirst} center>
          {' '}
          {[alertBanner.startDate, alertBanner.endDate]
            .filter((d): d is string => Boolean(d))
            .map((d) => new Date(d).toLocaleDateString(getLocaleString()))
            .join(' - ') || '-'}
        </TableCell>
      );
    case TableHeaderKeys.Status:
      return (
        <TableCell isFirst={isFirst} right>
          <Badge
            label={t(`table.status.states.${alertBanner.status}`)}
            color={stateColorMap[alertBanner.status] as keyof typeof colorMap}
          />
        </TableCell>
      );
    case TableHeaderKeys.Organization:
      return (
        <TableCell isFirst={isFirst} right>
          {alertBanner.organization.id !== activeOrganization.id ? (
            <Tooltip
              title={t('shared:inheritedTooltip', {
                model: t('alertBanners:alertBanner'),
                organization: translateBackend(
                  alertBanner.organization?.translatedName,
                ),
              })}
            >
              <Badge label={t('shared:inherited')} />
            </Tooltip>
          ) : (
            translateBackend(alertBanner.organization?.translatedName)
          )}
        </TableCell>
      );
    case TableHeaderKeys.Actions:
      return (
        <AlertBannerActionCell
          isFirst={isFirst}
          alertBanner={alertBanner}
          refetch={refetch}
        />
      );
  }
};
